import { defineStore } from 'pinia'
import { authService } from '@/services/auth'
import { ApiError } from '@/services/http'
import type {
  CodeRequestPayload,
  RegisterPayload,
  User,
  VerifyPayload,
  VerifyResult,
} from '@/types/api'

interface AuthState {
  user: User | null
  ready: boolean
  unreachable: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({ user: null, ready: false, unreachable: false }),

  getters: {
    isAuthenticated: (state): boolean => !!state.user,
    credits: (state): number => state.user?.credits ?? 0,
  },

  actions: {
    async fetchMe(): Promise<void> {
      try {
        this.user = await authService.me()
        this.unreachable = false
      } catch (e) {
        const status = e instanceof ApiError ? e.status : undefined
        if (status === 401) {
          this.user = null
          this.unreachable = false
        } else {
          this.unreachable = true
        }
      } finally {
        this.ready = true
      }
    },

    applyCredits(credits: number): void {
      if (this.user) this.user.credits = credits
    },

    clearSession(): void {
      this.user = null
      this.ready = false
      this.unreachable = false
    },

    async requestCode(payload: CodeRequestPayload): Promise<void> {
      await authService.requestCode(payload)
    },

    async verifyCode(payload: VerifyPayload): Promise<VerifyResult> {
      const result = await authService.verifyCode(payload)
      if (result.status === 'signed_in') {
        this.user = result.user
        this.ready = true
        this.unreachable = false
      }
      return result
    },

    async register(payload: RegisterPayload): Promise<void> {
      this.user = await authService.register(payload)
      this.ready = true
      this.unreachable = false
    },

    async logout(): Promise<void> {
      try {
        await authService.logout()
      } finally {
        this.clearSession()
      }
    },

    async requestDeletionCode(): Promise<void> {
      await authService.requestDeletionCode()
    },

    async deleteAccount(code: string): Promise<void> {
      await authService.deleteAccount(code)
      this.clearSession()
    },
  },
})
